import { useState } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function ContractSign() {
  const { user, currentCompany, setPage, refreshUser } = useApp();
  const { t } = useI18n();
  const [checks, setChecks] = useState([false, false, false]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [signed, setSigned] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const allChecked = checks.every(Boolean);

  async function doSign() {
    if (!allChecked || loading) return;
    
    // Validate company
    if (!currentCompany?.id) {
      setMsg('❌ Фирма не назначена. Обратитесь к администратору.');
      return;
    }
    
    setLoading(true);
    setMsg('');
    try {
      const { data } = await api.post('/contracts/sign', {
        company_id: currentCompany.id,
        terms_accepted: true,
      });
      setPdfUrl(data.pdf_url ? `https://api.workifypl.com${data.pdf_url}` : null);
      setSigned(true);
      if (refreshUser) await refreshUser();
    } catch (e) {
      // Better error messages
      if (e.response?.status === 403) {
        setMsg('❌ Профиль не верифицирован. Обратитесь к администратору.');
      } else if (e.response?.status === 400) {
        setMsg('❌ ' + (e.response?.data?.detail || 'Договор уже подписан'));
      } else if (!navigator.onLine) {
        setMsg('❌ Нет подключения к интернету');
      } else {
        setMsg('❌ ' + (e.userMessage || e.response?.data?.detail || 'Ошибка при подписании'));
      }
    } finally {
      setLoading(false);
    }
  }

  if (signed) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 72, marginBottom: 16 }}>✅</div>
        <h2 style={{ marginBottom: 8 }}>Договор подписан!</h2>
        <p style={{ opacity: 0.7, marginBottom: 8 }}>
          Доступ к фактурам и выводу средств открыт
        </p>
        <p style={{ opacity: 0.5, fontSize: 13, marginBottom: 24 }}>
          {currentCompany?.short_name || currentCompany?.name}
        </p>
        <div className="actions" style={{ gap: 10 }}>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
              style={{ display: 'block', textAlign: 'center' }}
            >
              📄 Скачать PDF договора
            </a>
          )}
          <button className="btn btn-primary" onClick={() => setPage('invoices_out')}>
            📄 Создать фактуру
          </button>
          <button className="btn btn-secondary" onClick={() => setPage('dashboard')}>
            🏠 На главную
          </button>
        </div>
      </div>
    );
  }

  // Show error if no company
  if (!currentCompany?.id) {
    return (
      <div className="page">
        <h1>📝 Договор сотрудничества</h1>
        <div style={{
          background: 'rgba(255,107,107,0.2)',
          border: '1px solid rgba(255,107,107,0.5)',
          padding: 16,
          borderRadius: 8,
          marginBottom: 16,
        }}>
          ❌ Фирма не назначена. Обратитесь к администратору для привязки к фирме.
        </div>
        <button className="btn btn-secondary" onClick={() => setPage('dashboard')}>
          ← На главную
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <h1>📝 Договор сотрудничества</h1>
      <p style={{ opacity: 0.7, marginBottom: 16 }}>
        Для выставления фактур через{' '}
        <b>{currentCompany?.short_name || currentCompany?.name || 'фирму'}</b>{' '}
        необходимо подписать договор
      </p>

      {/* Terms card */}
      <div className="detail-card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>💰 Условия сотрудничества</h3>
        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ opacity: 0.7 }}>Разовый платёж</span>
          <b>1 200 PLN</b>
        </div>
        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ opacity: 0.7 }}>Абонентская плата</span>
          <b>350 PLN/мес</b>
        </div>
        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ opacity: 0.7 }}>Комиссия при выводе</span>
          <b>6%</b>
        </div>
        <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ opacity: 0.7 }}>Налог (50% KUP)</span>
          <b style={{ color: '#10b981' }}>~6%</b>
        </div>
        <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(16,185,129,0.1)', borderRadius: 8, fontSize: 13, color: '#10b981' }}>
          ✅ ZUS = 0 PLN · Полная легализация · Документы на карту побыта
        </div>
      </div>

      {/* Checkboxes */}
      <div style={{ marginBottom: 20 }}>
        {[
          'Я ознакомлен(а) с условиями сотрудничества (1200 + 350/мес + 6%) и согласен(на)',
          'Я даю согласие на обработку персональных данных для целей договора',
          'Я подтверждаю достоверность предоставленных данных профиля',
        ].map((text, i) => (
          <label
            key={i}
            style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={checks[i]}
              onChange={() => {
                const next = [...checks];
                next[i] = !next[i];
                setChecks(next);
              }}
              style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, accentColor: '#7c3aed' }}
            />
            <span style={{ fontSize: 14, lineHeight: 1.4 }}>{text}</span>
          </label>
        ))}
      </div>

      {msg && (
        <div className="banner banner-warning" style={{ marginBottom: 12 }}>
          {msg}
        </div>
      )}

      <div className="actions">
        <button
          className="btn btn-primary"
          onClick={doSign}
          disabled={!allChecked || loading}
          style={{ opacity: !allChecked ? 0.5 : 1 }}
        >
          {loading ? '⏳ Подписываем...' : '✍️ Подписать договор'}
        </button>
        <button className="btn btn-secondary" onClick={() => setPage('dashboard')}>
          Назад
        </button>
      </div>

      <p style={{ fontSize: 11, opacity: 0.35, marginTop: 16, textAlign: 'center' }}>
        Telegram ID: {user?.telegram_id} · {new Date().toLocaleDateString('ru-RU')}
      </p>
    </div>
  );
}
